import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import VCXEventFilterBar from '../../components/shared/VCXEventFilterBar';
// TEMPORARILY DISABLED - Performance issues in PROD
// import VCXEventStatsCards from '../../components/shared/VCXEventStatsCards';
import VCXEventDetailPanel from '../../components/admin/VCXEventDetailPanel';
import AlertMessage from '../../components/shared/alertMessage';
import {
  setPaginatedEventsData,
  setFilters,
  resetFilters,
  // setStats, // TEMPORARILY DISABLED
  setSelectedEvent,
  setPagination,
  setLoading,
  clearError,
} from '../../components/redux/slices/vcxEventsSlice';
import { selectCurrentUser } from '../../components/redux/slices/userSlice';
import { selectVCXEventsFilters, selectUserPreferencesLoaded, updateUserPreferenceKey, saveUserPreferences } from '../../components/redux/slices/userPreferencesSlice';

const VCXEventsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { domain, eventId, tab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const eventsData = useSelector((state) => state.vcxEvents.eventsData);
  const reduxFilters = useSelector((state) => state.vcxEvents.filters);
  // const stats = useSelector((state) => state.vcxEvents.stats); // TEMPORARILY DISABLED
  const selectedEvent = useSelector((state) => state.vcxEvents.selectedEvent);
  const pagination = useSelector((state) => state.vcxEvents.pagination);
  const loading = useSelector((state) => state.vcxEvents.loading);
  const error = useSelector((state) => state.vcxEvents.error);
  const currentUser = useSelector(selectCurrentUser);

  // User preferences
  const savedFilterPrefs = useSelector(selectVCXEventsFilters);
  const preferencesLoaded = useSelector(selectUserPreferencesLoaded);
  const allUserPreferences = useSelector((state) => state.userPreferences.data);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // const [statsLoading, setStatsLoading] = useState(false); // TEMPORARILY DISABLED
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [filtersModified, setFiltersModified] = useState(false);


  // Initialize filters from Redux state
  const [localFilters, setLocalFilters] = useState({
    ...reduxFilters,
    status: reduxFilters.status || 'active',
    outcome: reduxFilters.outcome !== undefined ? reduxFilters.outcome : ',new,in_progress',
    recent_unknown: reduxFilters.recent_unknown || 'hide'
  });

  // Fetch events
  const fetchEvents = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(clearError());

    try {
      const params = {
        ...reduxFilters,
        page: pagination.page,
        page_size: pagination.page_size,
      };

      // Clean up empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await adminApi.getVCXEvents(params);

      if (response.data) {
        const paginationData = {
          page: response.data.page || 1,
          page_size: response.data.page_size || 20,
          total_count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / (response.data.page_size || 20)),
        };

        dispatch(setPaginatedEventsData({
          events: response.data.results || response.data,
          pagination: paginationData,
        }));
      }
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to Load Events',
        message: err.message || 'An error occurred while fetching events'
      });
    } finally {
      dispatch(setLoading(false));
    }
  }, [reduxFilters, pagination.page, pagination.page_size, dispatch]);

  // Fetch stats - TEMPORARILY DISABLED - Performance issues in PROD
  const fetchStats = useCallback(async () => {
    // Stats fetching disabled to reduce load
  }, []);

  // Track if initial filters have been set
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Initialize filters from URL query params and set today's date - ONLY RUN ONCE
  useEffect(() => {
    // Skip if already initialized (prevents reset when URL changes for event detail navigation)
    if (filtersInitialized) return;

    const statusFromUrl = searchParams.get('status') || 'active';
    const outcomeFromUrl = searchParams.get('outcome');

    // Always set default date to today
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // If saved preferences are loaded and no URL params, use saved preferences
    if (preferencesLoaded && savedFilterPrefs && Object.keys(savedFilterPrefs).length > 0) {
      const hasUrlParams = searchParams.get('status') || searchParams.get('outcome');

      if (!hasUrlParams) {
        const mergedFilters = {
          ...reduxFilters,
          ...savedFilterPrefs,
          from_date: savedFilterPrefs.from_date || todayStr,
          to_date: savedFilterPrefs.to_date || todayStr
        };
        dispatch(setFilters(mergedFilters));
        setLocalFilters(mergedFilters);
        setFiltersModified(false);
        setFiltersInitialized(true);
        return;
      }
    }

    // Otherwise use URL params or defaults with today's date
    const initialFilters = {
      ...reduxFilters,
      status: statusFromUrl,
      outcome: outcomeFromUrl !== null ? outcomeFromUrl : (statusFromUrl === 'active' ? ',new,in_progress' : ''),
      from_date: todayStr,
      to_date: todayStr
    };

    dispatch(setFilters(initialFilters));
    setLocalFilters(initialFilters);
    setFiltersInitialized(true);
  }, [preferencesLoaded, savedFilterPrefs, filtersInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL query params with filters
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Only add status if not the default 'active'
    if (reduxFilters.status && reduxFilters.status !== 'active') {
      newParams.set('status', reduxFilters.status);
    }

    // Don't show outcome in URL for default active view (outcome=,new,in_progress is default)
    // This keeps the URL clean for the default active view
    if (reduxFilters.outcome && reduxFilters.outcome !== ',new,in_progress') {
      newParams.set('outcome', reduxFilters.outcome);
    }

    // Update URL without triggering navigation
    const newSearch = newParams.toString();
    const currentSearch = searchParams.toString();

    if (newSearch !== currentSearch) {
      setSearchParams(newParams, { replace: true });
    }
  }, [reduxFilters.status, reduxFilters.outcome]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply domain filter from URL when domain param changes
  useEffect(() => {
    if (domain && domain !== reduxFilters.call_domain) {
      // Domain in URL - apply the filter while preserving other filters
      const newFilters = { ...reduxFilters, call_domain: domain };
      dispatch(setFilters(newFilters));
      setLocalFilters(newFilters);
    } else if (!domain && reduxFilters.call_domain) {
      // No domain in URL but filter is set - clear only the domain filter
      const newFilters = { ...reduxFilters, call_domain: '' };
      dispatch(setFilters(newFilters));
      setLocalFilters(newFilters);
    }
  }, [domain, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle eventId in URL - fetch and open detail panel
  useEffect(() => {
    if (eventId) {
      const fetchAndOpenEvent = async () => {
        try {
          // Check if event is already in eventsData
          const existingEvent = eventsData.find(event => event.id === eventId);

          if (existingEvent) {
            // Event is already loaded, just open it
            dispatch(setSelectedEvent(existingEvent));
            setSidebarOpen(true);
          } else {
            // Fetch the event from server
            const response = await adminApi.getVCXEvent(eventId);

            if (response.data) {
              dispatch(setSelectedEvent(response.data));
              setSidebarOpen(true);
            }
          }
        } catch (error) {
          setAlertState({
            show: true, type: 'error', title: 'Failed to Load Event',
            message: error.response?.data?.message || error.response?.data?.error || `Could not load event: ${eventId}`
          });
        }
      };

      fetchAndOpenEvent();
    }
  }, [eventId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset edit mode when navigating to different event
  useEffect(() => {
    setEditMode(false);
  }, [selectedEvent?.id]);

  useEffect(() => {
    fetchEvents();
    // fetchStats(); // TEMPORARILY DISABLED - Performance issues in PROD
  }, [reduxFilters, pagination.page, fetchEvents, fetchStats]);

  // Actions Cell Renderer
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="d-flex align-items-center gap-1" style={{ height: '100%' }}>
        <button
          className="btn btn-sm btn-danger"
          onClick={() => handleTakeAction(data)}
          title="Take Action"
        >
          <i className="fa fa-bolt"></i>
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleViewDetails(data)}
          title="View Details"
        >
          <i className="fa fa-eye"></i>
        </button>
        {data.is_active === false ? (
          <button
            className="btn btn-sm btn-outline-success"
            onClick={() => handleRestoreEvent(data)}
            title="Restore this archived event"
          >
            <i className="fas fa-undo"></i>
          </button>
        ) : (
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={() => handleDeleteEvent(data)}
            title="Archive Event"
          >
            <i className="fas fa-archive"></i>
          </button>
        )}
      </div>
    );
  };

  // Channel Badge Renderer
  const ChannelBadgeRenderer = (props) => {
    const channel = props.value;
    const colors = {
      phone: 'primary',
      sms: 'info',
      email: 'success',
      ehr_message: 'warning',
      teams: 'secondary',
      fax: 'dark',
      in_person: 'danger',
      other: 'light',
    };
    return <span className={`badge bg-${colors[channel] || 'secondary'}`}>{channel}</span>;
  };

  // Direction Badge Renderer
  const DirectionBadgeRenderer = (props) => {
    const direction = props.value;
    const colors = {
      inbound: 'success',
      outbound: 'primary',
      internal: 'info',
    };
    return <span className={`badge bg-${colors[direction] || 'secondary'}`}>{direction}</span>;
  };

  // Outcome Badge Renderer
  const OutcomeBadgeRenderer = (props) => {
    const outcome = props.value;
    if (!outcome) return <span className="text-muted">—</span>;

    const outcomeConfig = {
      new: { icon: 'fa-exclamation-circle', color: 'warning opacity-50 text-black', label: 'New' },
      in_progress: { icon: 'fa-clock', color: 'info', label: 'In Progress' },
      resolved: { icon: 'fa-check-circle', color: 'success', label: 'Resolved' },
    };

    const config = outcomeConfig[outcome] || { icon: 'fa-question-circle', color: 'secondary', label: outcome };
    return (
      <span className={`badge bg-${config.color}`} title={config.label}>
        <i className={`fa ${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  // Priority Icon Renderer
  const PriorityIconRenderer = (props) => {
    const priority = props.data?.event_metadata?.priority;
    if (!priority) return <span className="text-muted">—</span>;

    const priorityConfig = {
      urgent: { icon: 'fa-exclamation-circle', color: 'danger', label: 'Urgent' },
      high: { icon: 'fa-arrow-up', color: 'warning', label: 'High' },
      medium: { icon: 'fa-equals', color: 'info', label: 'Medium' },
      low: { icon: 'fa-arrow-down', color: 'secondary', label: 'Low' },
    };

    const config = priorityConfig[priority] || { icon: 'fa-minus', color: 'secondary', label: 'Normal' };
    return (
      <span className={`text-${config.color}`} title={config.label}>
        <i className={`fa ${config.icon} fa-lg`}></i>
      </span>
    );
  };

  // Domain configuration for display labels
  const domainConfig = {
    appointments: { emoji: '📅', label: 'Appointments', color: 'primary' },
    medications: { emoji: '💊', label: 'Medications', color: 'success' },
    medicalIssues: { emoji: '🏥', label: 'Medical Issues', color: 'danger' },
    billingInsurance: { emoji: '💳', label: 'Billing Insurance', color: 'warning' },
    medicalRecords: { emoji: '📋', label: 'Medical Records', color: 'info' },
    new_patient: { emoji: '🆕', label: 'New Patient', color: 'info' },
    generalInquiries: { emoji: 'ℹ️', label: 'General', color: 'secondary' },
    providerCalls: { emoji: '👨‍⚕️', label: 'Provider', color: 'dark' },
    vendorCalls: { emoji: '📞', label: 'Vendor', color: 'light' },
    unknown: { emoji: '❓', label: 'Unknown', color: 'secondary' }
  };

  const getDomainLabel = (domainValue) => {
    if (!domainValue) return '';
    return domainConfig[domainValue]?.label || domainValue.charAt(0).toUpperCase() + domainValue.slice(1);
  };

  // Assigned To Icon Renderer
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const AssignedToIconRenderer = (props) => {
    const assignedToId = props.data?.event_metadata?.assigned_to;
    const assignedToName = props.data?.event_metadata?.assigned_to_name;

    if (!assignedToId) return <span className="text-muted">—</span>;

    return (
      <button
        className="btn btn-sm btn-link p-0"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedUser({
            id: assignedToId,
            name: assignedToName || 'Unknown User'
          });
          setUserModalOpen(true);
        }}
        title={assignedToName || 'View user details'}
      >
        <i className="fa fa-user-circle fa-lg text-primary"></i>
      </button>
    );
  };

  // Call Domain Badge Renderer
  const CallDomainBadgeRenderer = (props) => {
    const domain = props.value;
    if (!domain) return <span className="text-muted">N/A</span>;

    const domainConfig = {
      appointments: { emoji: '📅', label: 'Appointments', color: 'primary' },
      medications: { emoji: '💊', label: 'Medications', color: 'success' },
      medicalIssues: { emoji: '🏥', label: 'Medical Issues', color: 'danger' },
      billingInsurance: { emoji: '💳', label: 'Billing', color: 'warning' },
      medicalRecords: { emoji: '📋', label: 'Records', color: 'info' },
      new_patient: { emoji: '🆕', label: 'New Patient', color: 'info' },
      generalInquiries: { emoji: 'ℹ️', label: 'General', color: 'secondary' },
      providerCalls: { emoji: '👨‍⚕️', label: 'Provider', color: 'dark' },
      vendorCalls: { emoji: '📞', label: 'Vendor', color: 'light' },
      unknown: { emoji: '❓', label: 'Unknown', color: 'secondary' }
    };

    const config = domainConfig[domain] || { emoji: '', label: domain, color: 'secondary' };
    return (
      <span className={`badge bg-${config.color}`} title={config.label}>
        {config.emoji} {config.label}
      </span>
    );
  };

  // Column Definitions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnDefs = useMemo(() => [
    {
      headerName: 'Actions',
      maxWidth: 130,
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      pinned: 'left',
      filter: false,
      editable: false,
    },
    {
      headerName: 'Channel',
      field: 'channel',
      maxWidth: 100,
      cellRenderer: ChannelBadgeRenderer,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['phone', 'sms', 'email', 'ehr_message', 'teams', 'fax', 'in_person', 'other']
      },
    },
    {
      headerName: 'Outcome',
      field: 'outcome',
      minWidth: 150,
      cellRenderer: OutcomeBadgeRenderer,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['', 'new', 'in_progress', 'resolved']
      },
    },
    {
      headerName: 'Priority',
      field: 'event_metadata.priority',
      maxWidth: 90,
      cellRenderer: PriorityIconRenderer,
      cellStyle: { textAlign: 'center' },
      sortable: true,
      filter: false,
    },
    {
      headerName: 'Assigned',
      field: 'event_metadata.assigned_to',
      maxWidth: 90,
      cellRenderer: AssignedToIconRenderer,
      cellStyle: { textAlign: 'center' },
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Follow-up',
      field: 'follow_up_required',
      maxWidth: 110,
      cellRenderer: (params) => {
        const followUps = params.data?.event_metadata?.follow_ups || [];
        const hasFollowUp = params.value || followUps.length > 0;
        const count = followUps.length;

        if (!hasFollowUp) {
          return <span className="text-muted">—</span>;
        }

        return (
          <span className="badge bg-warning" title={`${count} follow-up${count !== 1 ? 's' : ''} required`}>
            <i className="fa fa-bell me-1"></i>
            {count > 0 ? count : 'Yes'}
          </span>
        );
      },
      editable: false,
      sortable: true,
    },
    {
      headerName: 'Caller/Patient',
      field: 'customer_name',
      minWidth: 180,
      editable: true,
      cellRenderer: (params) => {
        const data = params.data;
        if (!data) return '';

        // Priority: manually edited data > AI-extracted data > legacy fields
        let displayName = '';
        let hasManualData = false;
        let hasWebhookData = false;
        let tooltipText = '';

        // 1. Check manually edited patient data (highest priority)
        if (data.event_metadata?.patient_data?.patient_first_name || data.event_metadata?.patient_data?.patient_last_name) {
          displayName = [data.event_metadata.patient_data.patient_first_name, data.event_metadata.patient_data.patient_last_name]
            .filter(Boolean)
            .join(' ');
          hasManualData = true;

          const tooltipParts = [];
          if (data.event_metadata.patient_data.patient_dob) {
            tooltipParts.push(`DOB: ${data.event_metadata.patient_data.patient_dob}`);
          }
          if (data.event_metadata.patient_data.match_patient_id) {
            tooltipParts.push(`EHR ID: ${data.event_metadata.patient_data.match_patient_id}`);
          }
          // Show caller if different from patient
          if (data.event_metadata?.caller_data?.caller_first_name || data.event_metadata?.caller_data?.caller_last_name) {
            const callerName = [data.event_metadata.caller_data.caller_first_name, data.event_metadata.caller_data.caller_last_name].filter(Boolean).join(' ');
            tooltipParts.push(`Caller: ${callerName}`);
          }
          tooltipText = tooltipParts.join(' | ');
        }
        // 2. Check AI-extracted patient data
        else if (data.extracted_patient_data?.patient_first_name || data.extracted_patient_data?.patient_last_name) {
          displayName = [data.extracted_patient_data.patient_first_name, data.extracted_patient_data.patient_last_name]
            .filter(Boolean)
            .join(' ');
          hasWebhookData = true;

          const tooltipParts = [];
          if (data.extracted_patient_data.patient_dob) {
            tooltipParts.push(`DOB: ${data.extracted_patient_data.patient_dob}`);
          }
          if (data.extracted_patient_data.match_patient_id) {
            tooltipParts.push(`EHR ID: ${data.extracted_patient_data.match_patient_id}`);
          }
          if (data.extracted_patient_data.caller_first_name || data.extracted_patient_data.caller_last_name || data.extracted_patient_data.caller_name) {
            const callerName = data.extracted_patient_data.caller_first_name || data.extracted_patient_data.caller_last_name
              ? [data.extracted_patient_data.caller_first_name, data.extracted_patient_data.caller_last_name].filter(Boolean).join(' ')
              : data.extracted_patient_data.caller_name;
            tooltipParts.push(`Caller: ${callerName}`);
          }
          tooltipText = tooltipParts.join(' | ');
        }
        // 3. Check manually edited caller data
        else if (data.event_metadata?.caller_data?.caller_first_name || data.event_metadata?.caller_data?.caller_last_name) {
          displayName = [data.event_metadata.caller_data.caller_first_name, data.event_metadata.caller_data.caller_last_name]
            .filter(Boolean)
            .join(' ');
          hasManualData = true;
        }
        // 4. Check AI-extracted caller data
        else if (data.extracted_patient_data?.caller_name || data.extracted_patient_data?.caller_first_name || data.extracted_patient_data?.caller_last_name) {
          displayName = data.extracted_patient_data.caller_first_name || data.extracted_patient_data.caller_last_name
            ? [data.extracted_patient_data.caller_first_name, data.extracted_patient_data.caller_last_name].filter(Boolean).join(' ')
            : data.extracted_patient_data.caller_name;
          hasWebhookData = true;

          const tooltipParts = [];
          if (data.extracted_patient_data.caller_details) {
            tooltipParts.push(data.extracted_patient_data.caller_details);
          }
          tooltipText = tooltipParts.join(' | ');
        }
        // 5. Legacy fields
        else {
          displayName = data.customer_name || data.patient_name || 'Not provided';
        }

        return (
          <div className="d-flex align-items-center gap-1" title={tooltipText || displayName}>
            <span>{displayName}</span>
            {hasManualData && (
              <span className="badge bg-success" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                ✓
              </span>
            )}
            {hasWebhookData && (
              <span className="badge bg-info" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                AI
              </span>
            )}
          </div>
        );
      },
      valueGetter: (params) => {
        const data = params.data;
        // Same priority as cellRenderer: manually edited > AI-extracted > legacy
        if (data?.event_metadata?.patient_data?.patient_first_name || data?.event_metadata?.patient_data?.patient_last_name) {
          return [data.event_metadata.patient_data.patient_first_name, data.event_metadata.patient_data.patient_last_name]
            .filter(Boolean)
            .join(' ');
        }
        if (data?.extracted_patient_data?.patient_first_name || data?.extracted_patient_data?.patient_last_name) {
          return [data.extracted_patient_data.patient_first_name, data.extracted_patient_data.patient_last_name]
            .filter(Boolean)
            .join(' ');
        }
        if (data?.event_metadata?.caller_data?.caller_first_name || data?.event_metadata?.caller_data?.caller_last_name) {
          return [data.event_metadata.caller_data.caller_first_name, data.event_metadata.caller_data.caller_last_name]
            .filter(Boolean)
            .join(' ');
        }
        if (data?.extracted_patient_data?.caller_name || data?.extracted_patient_data?.caller_first_name || data?.extracted_patient_data?.caller_last_name) {
          return data.extracted_patient_data.caller_first_name || data.extracted_patient_data.caller_last_name
            ? [data.extracted_patient_data.caller_first_name, data.extracted_patient_data.caller_last_name].filter(Boolean).join(' ')
            : data.extracted_patient_data.caller_name;
        }
        return data?.customer_name || data?.patient_name || 'Not provided';
      },
      valueSetter: (params) => {
        params.data.customer_name = params.newValue;
        return true;
      },
    },
    {
      headerName: 'Phone',
      field: 'customer_phone',
      minWidth: 130,
      editable: true,
    },
    {
      headerName: 'Direction',
      field: 'direction',
      maxWidth: 100,
      cellRenderer: DirectionBadgeRenderer,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['inbound', 'outbound', 'internal']
      },
    },
    // {
    //   headerName: 'Email',
    //   field: 'customer_email',
    //   minWidth: 150,
    //   editable: true,
    // },
    {
      headerName: 'Caller Type',
      field: 'customer_type',
      minWidth: 130,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['patient', 'family', 'vendor', 'insurance', 'other_provider']
      },
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return params.value.replace(/_/g, ' ');
      },
    },
    {
      headerName: 'Health System',
      field: 'health_system_name',
      minWidth: 150,
      editable: false,
    },
    {
      headerName: 'Agent',
      field: 'voiceai_agent_name',
      maxWidth: 100,
      editable: false,
      valueGetter: (params) => {
        if (params.data?.voiceai_agent_name) return params.data.voiceai_agent_name;
        if (params.data?.performed_by_name) return params.data.performed_by_name;
        return params.data?.is_automated ? 'Automated' : 'N/A';
      },
    },
    {
      headerName: 'Domain',
      field: 'call_domain',
      minWidth: 150,
      cellRenderer: CallDomainBadgeRenderer,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['', 'appointments', 'medications', 'medicalIssues', 'billingInsurance', 'medicalRecords', 'new_patient', 'generalInquiries', 'providerCalls', 'vendorCalls', 'unknown']
      },
    },
    // {
    //   headerName: 'Caller Intent',
    //   field: 'caller_intent',
    //   minWidth: 150,
    //   editable: true,
    // },
    // {
    //   headerName: 'Action',
    //   field: 'action',
    //   minWidth: 150,
    //   editable: true,
    // },
    {
      headerName: 'Created',
      field: 'created_at',
      minWidth: 160,
      editable: false,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString();
      },
    },
  ], []);

  // Default column definition - memoized to prevent grid reinitialization
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  // Row style - memoized to prevent grid reinitialization
  const rowStyle = useMemo(() => ({ cursor: 'pointer' }), []);

  // Layout callbacks - memoized to prevent unnecessary re-renders
  const handleLayoutSaved = useCallback(() => {
    setAlertState({
      show: true,
      type: 'success',
      title: '',
      message: 'Table layout has been saved to your preferences'
    });
  }, []);

  const handleLayoutReset = useCallback(() => {
    setAlertState({
      show: true,
      type: 'info',
      title: '',
      message: 'Table layout has been reset to your saved preferences'
    });
  }, []);

  // Filter configuration
  const filterConfig = useMemo(() => {
    const filters = [
      {
        type: 'select',
        name: 'channel',
        options: [
          { value: '', label: 'All Channels' },
          { value: 'phone', label: 'Phone' },
          { value: 'sms', label: 'SMS' },
          { value: 'email', label: 'Email' },
          { value: 'ehr_message', label: 'EHR Message' },
          { value: 'teams', label: 'Teams' },
          { value: 'fax', label: 'Fax' },
          { value: 'in_person', label: 'In Person' },
          { value: 'other', label: 'Other' }
        ],
        width: 'auto'
      },
      {
        type: 'select',
        name: 'direction',
        options: [
          { value: '', label: 'All Directions' },
          { value: 'inbound', label: 'Inbound' },
          { value: 'outbound', label: 'Outbound' },
          { value: 'internal', label: 'Internal' }
        ],
        width: 'auto'
      },
      {
        type: 'select',
        name: 'call_domain',
        options: [
          { value: '', label: 'All Call Types' },
          { value: 'appointments', label: '📅 Appointments' },
          { value: 'medications', label: '💊 Medications' },
          { value: 'medicalIssues', label: '🏥 Medical Issues' },
          { value: 'billingInsurance', label: '💳 Billing/Insurance' },
          { value: 'medicalRecords', label: '📋 Medical Records' },
          { value: 'new_patient', label: '🆕 New Patient' },
          { value: 'generalInquiries', label: 'ℹ️ General Inquiries' },
          { value: 'providerCalls', label: '👨‍⚕️ Provider Calls' },
          { value: 'vendorCalls', label: '📞 Vendor Calls' },
          { value: 'unknown', label: '❓ Unknown/Other' }
        ],
        width: 'auto'
      },
    ];

    // Show recent_unknown filter only for Ankr admins when viewing All domains or Unknown domain specifically
    if (currentUser?.isAnkrAdmin && (!localFilters.call_domain || localFilters.call_domain === 'unknown')) {
      filters.push({
        type: 'select',
        name: 'recent_unknown',
        options: [
          { value: 'hide', label: 'Hide Recent Unknown' },
          { value: 'include', label: 'Show All Unknown' }
        ],
        width: 'auto'
      });
    }

    // Add remaining filters
    filters.push(
      {
        type: 'select',
        name: 'customer_type',
        options: [
          { value: '', label: 'All Customer Types' },
          { value: 'patient', label: 'Patient' },
          { value: 'family', label: 'Family' },
          { value: 'vendor', label: 'Vendor' },
          { value: 'insurance', label: 'Insurance' },
          { value: 'other_provider', label: 'Other Provider' }
        ],
        width: 'auto'
      },
      {
        type: 'select',
        name: 'outcome',
        options: [
          { value: '', label: 'All Outcomes' },
          { value: 'new', label: '⚠ New' },
          { value: 'in_progress', label: '⏱ In Progress' },
          { value: 'resolved', label: '✓ Resolved' }
        ],
        width: 'auto'
      },
      {
        type: 'select',
        name: 'priority',
        options: [
          { value: '', label: 'All Priorities' },
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' }
        ],
        width: 'auto'
      },
      {
        type: 'select',
        name: 'follow_up_required',
        options: [
          { value: '', label: 'All Follow-ups' },
          { value: 'true', label: 'Requires Follow-up' },
          { value: 'false', label: 'No Follow-up' }
        ],
        width: 'auto'
      },
      {
        type: 'daterange',
        name: 'date_range',
        placeholder: 'Select date range',
        width: '250px'
      },
      {
        type: 'text',
        name: 'search',
        placeholder: 'First or last name, phone, email...',
        width: '200px'
      }
    );

    return filters;
  }, [localFilters.call_domain, currentUser?.isAnkrAdmin]);

  // Handlers
  const handleFilterChange = (name, value) => {
    // Update local state only - don't trigger search yet
    // Use functional form to avoid stale closure issues
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    // Validate date range if both dates are provided
    if (localFilters.from_date && localFilters.to_date) {
      if (new Date(localFilters.from_date) > new Date(localFilters.to_date)) {
        setAlertState({
          show: true,
          type: 'error',
          title: 'Invalid Date Range',
          message: 'Start date must be less than or equal to end date'
        });
        return;
      }
    }

    // Commit local filters to Redux and trigger search
    dispatch(setFilters(localFilters));
    dispatch(setPagination({ page: 1 })); // Reset to first page when searching
    setFiltersModified(true); // Mark filters as modified
  };

  const handleResetFilters = () => {
    // Set default date to today
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const defaultFilters = { status: 'active', outcome: ',new,in_progress', from_date: todayStr, to_date: todayStr, assigned_to: '', recent_unknown: 'hide' };

    dispatch(resetFilters());
    dispatch(setFilters(defaultFilters));
    setLocalFilters(defaultFilters);
    setFiltersModified(true); // Mark as modified when resetting
  };

  const handleSaveAsDefault = () => {
    // Save current filters to user preferences (excluding transient/context-specific fields)
    const filtersToSave = { ...reduxFilters };
    delete filtersToSave.search; // Don't persist search text
    delete filtersToSave.call_domain; // Don't persist call domain (context-specific)

    // Build the updated preferences object with vcx_events_filters
    const updatedPreferences = {
      ...allUserPreferences,
      vcx_events_filters: filtersToSave
    };

    // Update Redux state
    dispatch(updateUserPreferenceKey({
      key: 'vcx_events_filters',
      value: filtersToSave,
      merge: false
    }));

    // Save to backend
    dispatch(saveUserPreferences(updatedPreferences)).then(() => {
      setFiltersModified(false); // Reset modified flag after successful save
      setAlertState({
        show: true,
        type: 'success',
        title: '',
        message: 'Filter settings saved as your default preferences'
      });
    }).catch(() => {
      setAlertState({
        show: true,
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save filter preferences'
      });
    });
  };

  const handleViewDetails = (event) => {
    dispatch(setSelectedEvent(event));
    setSidebarOpen(true);

    // Navigate to event URL, preserving query params
    const basePath = domain ? `/admin/vcx-events/domain/${domain}/${event.id}` : `/admin/vcx-events/${event.id}`;
    const currentSearch = searchParams.toString();
    const newPath = currentSearch ? `${basePath}?${currentSearch}` : basePath;
    navigate(newPath, { replace: true });
  };

  const handleTakeAction = (event) => {
    dispatch(setSelectedEvent(event));
    setOpenActivityModal(true);
    setSidebarOpen(true);

    // Navigate to activities tab using path param
    const basePath = domain ? `/admin/vcx-events/domain/${domain}/${event.id}/activities` : `/admin/vcx-events/${event.id}/activities`;
    const currentSearch = searchParams.toString();
    const newPath = currentSearch ? `${basePath}?${currentSearch}` : basePath;
    navigate(newPath, { replace: true });
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setOpenActivityModal(false);
    dispatch(setSelectedEvent(null));

    // Navigate back to the events list URL, preserving query params
    const basePath = domain ? `/admin/vcx-events/domain/${domain}` : '/admin/vcx-events';
    const currentSearch = searchParams.toString();
    const newPath = currentSearch ? `${basePath}?${currentSearch}` : basePath;
    navigate(newPath, { replace: true });
  };

  const handleNavigateEvent = (direction) => {
    if (!selectedEvent || !eventsData.length) return;

    const currentIndex = eventsData.findIndex(e => e.id === selectedEvent.id);
    if (currentIndex === -1) return;

    let nextIndex;
    if (direction === 'next') {
      nextIndex = currentIndex + 1;
      if (nextIndex >= eventsData.length) return; // At last event
    } else {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) return; // At first event
    }

    dispatch(setSelectedEvent(eventsData[nextIndex]));
  };

  const handleDeleteEvent = async (event) => {
    const confirmMessage = `Are you sure you want to archive this event?\n\nThis will hide the event from the active list but can be restored later.\n\nCustomer: ${event.customer_name || event.patient_name || 'N/A'}\nChannel: ${event.channel}\nCreated: ${new Date(event.created_at).toLocaleString()}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await adminApi.deleteVCXEvent(event.id);

      // Refresh events after archiving
      await fetchEvents();
      // await fetchStats(); // TEMPORARILY DISABLED

      // Close sidebar if the archived event was selected
      if (selectedEvent?.id === event.id) {
        handleCloseSidebar();
      }

      setAlertState({ show: true, type: 'success', title: '', message: 'Event archived successfully' });
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Archive Failed', message: err.response?.data?.error || 'Failed to archive event' });
    }
  };

  const handleRestoreEvent = async (event) => {
    const confirmMessage = `Restore this archived event?\n\nThis will make the event visible in the active list again.\n\nCustomer: ${event.customer_name || event.patient_name || 'N/A'}\nChannel: ${event.channel}\nCreated: ${new Date(event.created_at).toLocaleString()}`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      await adminApi.restoreVCXEvent(event.id);
      // Refresh events after restoring
      await fetchEvents();
      // await fetchStats(); // TEMPORARILY DISABLED
      setAlertState({ show: true, type: 'success', title: '', message: 'Event restored successfully' });
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Restore Failed', message: err.response?.data?.error || 'Failed to restore event' });
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;

      await adminApi.updateVCXEvent(data.id, { [colDef.field]: newValue });
      await fetchEvents();
      // await fetchStats(); // TEMPORARILY DISABLED
      setAlertState({ show: true, type: 'success', title: '', message: 'Event updated successfully' });
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Update Failed', message: err.response?.data?.message || 'Failed to update event' });
      await fetchEvents(); // Revert changes by refetching
    }
  };

  const handleEventUpdate = async (updatedEvent) => {
    // Update the selected event FIRST with the fresh data
    if (updatedEvent) {
      dispatch(setSelectedEvent(updatedEvent));
    }

    // Then refresh the events list and stats
    await fetchEvents();
    // await fetchStats(); // TEMPORARILY DISABLED
  };

  const handlePageChange = (newPage) => {
    dispatch(setPagination({ page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPagination({ page: 1, page_size: newPageSize }));
  };


  return (
    <div className="container-fluid mt-4">
      <div className="page-title-box d-flex align-items-center justify-content-between">
        <div className="col-8">
          <h4 className="text-lg">
            VCX Events{domain && ` - ${getDomainLabel(domain)}`}
          </h4>
          <p className="text-muted">Central resource to track multi-channel interactions with your customers</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/">Admin</a></li>
              <li className="breadcrumb-item">
                <a href="/admin/vcx-events">VCX Events</a>
              </li>
              {domain && (
                <li className="breadcrumb-item active">{getDomainLabel(domain)}</li>
              )}
              {!domain && (
                <li className="breadcrumb-item active">All Domains</li>
              )}
            </ol>
          </nav>
        </div>
      </div>

      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearError())}
          ></button>
        </div>
      )}

      {/* TEMPORARILY DISABLED - Performance issues in PROD */}
      {/* <VCXEventStatsCards stats={stats} loading={statsLoading} /> */}

      <div className="card">
        <div className="row d-flex align-items-center gy-3">
          <div className="col-9">
            <VCXEventFilterBar
              filters={{ ...localFilters, hasChanges: filtersModified }}
              appliedFilters={reduxFilters}
              filterConfig={filterConfig}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onClear={handleResetFilters}
              onSaveAsDefault={handleSaveAsDefault}
              loading={loading}
              searchButtonText="Apply Filters"
              showClearButton={true}
              showSaveButton={true}
              showAssignedToMe={true}
              currentUserId={currentUser?.djangoUserId}
              containerStyle={{ marginBottom: 0 }}
            />
          </div>
          <div className="col-3 d-flex justify-content-between flex-wrap">
            <div className="btn-group btn-group-sm flex-wrap me-2 mb-2" role="group">
              <button
                type="button"
                className={`btn ${(localFilters.status === 'active' && localFilters.outcome === ',new,in_progress') ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  const newFilters = { ...localFilters, status: 'active', outcome: ',new,in_progress' };
                  setLocalFilters(newFilters);
                  dispatch(setFilters(newFilters));
                  setFiltersModified(true);
                }}
              >
                Active
              </button>
              <button
                type="button"
                className={`btn ${(localFilters.status === 'active' && localFilters.outcome === 'resolved') ? 'btn-success' : 'btn-outline-primary'}`}
                onClick={() => {
                  const newFilters = { ...localFilters, status: 'active', outcome: 'resolved' };
                  setLocalFilters(newFilters);
                  dispatch(setFilters(newFilters));
                  setFiltersModified(true);
                }}
              >
                Resolved
              </button>
              <button
                type="button"
                className={`btn ${localFilters.status === 'archive' ? 'btn-warning' : 'btn-outline-primary'}`}
                onClick={() => {
                  const newFilters = { ...localFilters, status: 'archive', outcome: '' };
                  setLocalFilters(newFilters);
                  dispatch(setFilters(newFilters));
                  setFiltersModified(true);
                }}
              >
                Archived
              </button>
            </div>
            <button className="btn btn-sm btn-primary mb-2 me-3" onClick={fetchEvents} disabled={loading}>
              <i className={`fa fa-refresh me-1 ${loading ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
          </div>
        </div>

        <AgGridTable
          rowData={eventsData}
          columnDefs={columnDefs}
          loading={loading}
          defaultColDef={defaultColDef}
          suppressPaginationPanel={true}
          enableLayoutSaving={true}
          gridId="vcx_events_grid"
          showLayoutControls={true}
          controlsPosition="top"
          onLayoutSaved={handleLayoutSaved}
          onLayoutReset={handleLayoutReset}
          onCellClicked={(params) => {
            // Don't open detail panel if clicking on Actions column (no field property)
            const colDef = params.column?.getColDef();
            if (!colDef?.field) {
              return;
            }
            handleViewDetails(params.data);
          }}
          onCellValueChanged={handleCellValueChanged}
          rowStyle={rowStyle}
        />

        {/* Custom Pagination Controls */}
        {pagination.total_count > 0 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="text-muted">
              Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} entries
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="text-muted mb-0">Rows per page:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: '80px' }}
                  value={pagination.page_size}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">
                  Page {pagination.page} of {pagination.total_pages || 1}
                </span>
                <div className="btn-group btn-group-sm">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    title="Previous page"
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= (pagination.total_pages || 1) || loading}
                    title="Next page"
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <VCXEventDetailPanel
        key={selectedEvent ? `${selectedEvent.id}-${selectedEvent.updated_at}` : 'no-event'}
        event={selectedEvent}
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        onUpdate={handleEventUpdate}
        openInEditMode={editMode}
        onEditStart={() => setEditMode(true)}
        onEditEnd={() => setEditMode(false)}
        openActivityModal={openActivityModal}
        onNavigate={handleNavigateEvent}
        currentIndex={selectedEvent ? eventsData.findIndex(e => e.id === selectedEvent.id) : -1}
        totalEvents={eventsData.length}
      />

      {/* User Details Modal */}
      {userModalOpen && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-user-circle me-2"></i>
                  Assigned User
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setUserModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fa fa-user-circle fa-5x text-primary mb-3"></i>
                  <h5>{selectedUser?.name}</h5>
                  <p className="text-muted">User ID: {selectedUser?.id}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setUserModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VCXEventsPage;
